import { InventoryItemDisposition } from "@redotech/crdb-enums/inventory/inventory-item-disposition";
import { InventoryItemOutcome } from "@redotech/crdb-enums/inventory/inventory-item-outcome";
import { SortDisposition } from "@redotech/crdb-enums/returns/sort-disposition";

/**
 * Where a returned unit stands for Supply Intelligence. Callers pass only
 * return-style units (no item class) that aren't committed to an order; lot
 * and custom items are fresh stock.
 */
export enum SupplyIntelPool {
  /** In a non-sellable bin in the damaged area. */
  DamagedArea = "damaged_area",
  /** In a sellable bin with a restock decision: for sale on the brand's store. */
  ForSale = "for_sale",
  /** In a non-sellable bin, such as a brand holding area. */
  HeldNonSellable = "held_non_sellable",
  /** In a sellable bin without a restock decision, so inventory reserves it. */
  ReservedInSellableBin = "reserved_in_sellable_bin",
}

export interface UnitDecisions {
  disposition: InventoryItemDisposition | null;
  outcome: InventoryItemOutcome | null;
  sortDisposition: SortDisposition | null;
}

/**
 * Inventory's restock rule (`redo/warehouse/db/util/src/reserved-bucket-derived.ts`):
 * the first non-null of outcome, sort disposition and legacy disposition
 * decides.
 */
function firstDecision({
  disposition,
  outcome,
  sortDisposition,
}: UnitDecisions):
  | InventoryItemDisposition
  | InventoryItemOutcome
  | SortDisposition
  | null {
  return outcome ?? sortDisposition ?? disposition;
}

/** All three enums spell restock as `"restock"`. */
export function isRestockDecided(decisions: UnitDecisions): boolean {
  return firstDecision(decisions) === InventoryItemOutcome.Restock;
}

/** The decision that already routes a unit away from restock, or null. */
export function supplyIntelRoutedTo(decisions: UnitDecisions): string | null {
  const decision = firstDecision(decisions);
  if (decision === null || decision === InventoryItemOutcome.Restock) {
    return null;
  }
  return decision === SortDisposition.Problem ? "problem review" : decision;
}

/** The system `DAMAGED` bin and SLC's `DAM-…` / `DAM …` damaged-area bins. */
const DAMAGED_AREA_BIN_NAME = /^DAM(?:AGED)?(?:[\s_-]|\d|$)/i;

interface SupplyIntelPoolInput {
  binIsSellable: boolean;
  binName: string;
  restockDecided: boolean;
}

export function classifySupplyIntelPool({
  binIsSellable,
  binName,
  restockDecided,
}: SupplyIntelPoolInput): SupplyIntelPool {
  if (binIsSellable) {
    return restockDecided
      ? SupplyIntelPool.ForSale
      : SupplyIntelPool.ReservedInSellableBin;
  }
  if (DAMAGED_AREA_BIN_NAME.test(binName)) {
    return SupplyIntelPool.DamagedArea;
  }
  return SupplyIntelPool.HeldNonSellable;
}
